import {
  activityCodeDescriptionMap,
  type EnrichedAuditEvent,
} from '@eppendorf/vnls-audit-event-client';
import { Icon, VirtualizedTable } from '@eppendorf/vnls-react-components';
import { createColumnHelper } from '@tanstack/react-table';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router-dom';

import { CellTimestamp } from '$components/cell-timestamp/cell-timestamp';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { useAuditEventsQuery } from '$features/audit-events/audit-events.api';
import { getIconNameForActivityCode as getIconPropsForActivityCode } from '$features/audit-events/audit-events.utils';
import { AuditEventDescription } from '$features/audit-events/components/description/description';
import { AuditEventCategory } from '$features/audit-events/components/filters/category-filter';
import { PerformedByCell } from '$features/audit-events/components/performed-by/performed-by-cell';

import './audit-events-list.scss';

type AuditEventsListProps = {
  from: string | null;
  to: string | null;
  searchTerm: string | null;
  category: AuditEventCategory | null;
  setSearchParams: Dispatch<SetStateAction<URLSearchParams>>;
};

export function AuditEventsList({
  from,
  to,
  searchTerm,
  category,
  setSearchParams,
}: AuditEventsListProps): ReactElement {
  const { t } = useTranslation();
  const routeError = useRouteError();
  const {
    data,
    isError,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useAuditEventsQuery(from, to, searchTerm, category);
  const auditLogs = data?.pages.flat() ?? [];
  const columnHelper = createColumnHelper<EnrichedAuditEvent>();
  const isLoadMorePending = useRef(false);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    if (isFetching) {
      isLoadMorePending.current = true;
      return;
    }

    isLoadMorePending.current = false;
    fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]);

  useEffect(() => {
    if (!hasNextPage) {
      isLoadMorePending.current = false;
      return;
    }

    if (!isFetching && isLoadMorePending.current) {
      isLoadMorePending.current = false;
      fetchNextPage({ cancelRefetch: false });
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  const setScrollerRef = useCallback((element: HTMLElement | Window | null) => {
    scrollerRef.current = element instanceof HTMLElement ? element : null;
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (
      scroller &&
      scroller.clientHeight > 0 &&
      scroller.scrollHeight <= scroller.clientHeight
    ) {
      loadMore();
    }
  }, [auditLogs.length, loadMore]);

  // make sure that opening the sidecar preserves the search params
  const handleRowClick = (event: EnrichedAuditEvent) => {
    setSearchParams((prev) => {
      prev.set('sidecarRoute', 'audit-log/detail');
      prev.set('id', event.id);
      return prev;
    });
  };

  /* eslint-disable react/no-unstable-nested-components -- accepted */
  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: t('auditEvents.header.timestamp'),
        cell: (info) => <CellTimestamp timestamp={info.renderValue()} />,
        meta: {
          dataLabel: t('auditEvents.header.timestamp'),
        },
      }),
      columnHelper.accessor('activityCode', {
        header: () => (
          <div className="flex flex__jc--center">{t('auditEvents.header.type')}</div>
        ),
        meta: {
          dataLabel: t('auditEvents.header.type'),
        },
        cell: (info) => {
          const { name, size } = getIconPropsForActivityCode(info.getValue());
          return (
            <div className="flex flex__jc--center">
              <Icon name={name} size={size} />
            </div>
          );
        },
      }),
      columnHelper.accessor('activityCode', {
        id: 'description-id',
        header: t('auditEvents.header.activity'),
        meta: {
          dataLabel: t('auditEvents.header.activity'),
        },
        cell: (info) => activityCodeDescriptionMap[info.getValue()],
      }),
      columnHelper.accessor('metadata', {
        id: 'performed-by',
        header: t('auditEvents.header.performedBy'),
        cell: (info) => <PerformedByCell entity={info.row.original} forDetail={false} />,
        meta: {
          cell: {
            className: 'w-max-l',
          },
          dataLabel: t('auditEvents.header.performedBy'),
        },
      }),
      columnHelper.accessor('metadata', {
        id: 'details-description',
        header: t('auditEvents.header.details'),
        cell: (info) => <AuditEventDescription event={info.row.original} />,
        meta: {
          cell: {
            className: 'w-max-l',
          },
          dataLabel: t('auditEvents.header.details'),
        },
      }),
    ],
    [t],
  );
  /* eslint-enable react/no-unstable-nested-components */
  const resetSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('from');
      prev.delete('to');
      prev.delete('searchTerm');
      prev.delete('category');
      return prev;
    });
  }, []);

  return (
    <>
      {(routeError || isError) && <LoadingErrorHint />}

      {!isLoading && !isError && (
        <div
          className="flex flex__dir--column"
          style={{
            height: auditLogs && auditLogs.length > 0 ? '100vh' : '50vh',
          }}
        >
          {auditLogs && (
            <VirtualizedTable
              data={auditLogs}
              columns={columns}
              loadMore={loadMore}
              scrollerRef={setScrollerRef}
              isLoading={isFetching}
              isTableToCardDesign
              onResetSearch={resetSearch}
              onRowClick={handleRowClick}
              className="table--audit-event-list"
            />
          )}
        </div>
      )}
    </>
  );
}
